"use client";
/*
 * Documentation:
 * TagClickLogStatus — https://app.subframe.com/ae91faa555e6/library?component=TagClickLogStatus_f7edaefb-dbee-4a86-8aa2-96473d550505
 */

import React from "react";
import * as SubframeUtils from "../utils";
import * as SubframeCore from "@subframe/core";

interface TagClickLogStatusRootProps
  extends React.HTMLAttributes<HTMLDivElement> {
  icon?: SubframeCore.IconName;
  variant?:
    | "default"
    | "clk-pending"
    | "clik-dismissed"
    | "clik-resolved"
    | "pending"
    | "resolved"
    | "dismissed";
  text?: React.ReactNode;
  className?: string;
}

const TagClickLogStatusRoot = React.forwardRef<
  HTMLElement,
  TagClickLogStatusRootProps
>(function TagClickLogStatusRoot(
  {
    icon = "FeatherCheck",
    variant = "default",
    text,
    className,
    ...otherProps
  }: TagClickLogStatusRootProps,
  ref
) {
  return (
    <div
      className={SubframeUtils.twClassNames(
        "group/f7edaefb flex h-6 items-center gap-1 overflow-hidden rounded-full bg-default-background px-2 max-w-fit whitespace-nowrap",
        {
          "border border-solid border-neutral-border bg-neutral-100":
            variant === "dismissed",
          "bg-success-100": variant === "resolved",
          "bg-warning-100": variant === "pending",
          "border border-solid border-neutral-border bg-white active:border active:border-solid active:border-success-100 active:bg-success-100 focus-within:border focus-within:border-solid focus-within:border-success-100 focus-within:bg-success-100":
            variant === "clik-resolved",
          "border border-solid border-neutral-border active:bg-neutral-100 focus-within:bg-neutral-100":
            variant === "clik-dismissed",
          "border border-solid border-neutral-border bg-white active:border active:border-solid active:border-warning-100 active:bg-warning-100 focus-within:border focus-within:border-solid focus-within:border-warning-100 focus-within:bg-warning-100":
            variant === "clk-pending",
        },
        className
      )}
      ref={ref as any}
      {...otherProps}
    >
      <SubframeCore.Icon
        className={SubframeUtils.twClassNames(
          "text-body font-body text-default-font",
          {
            hidden: variant === "dismissed" || variant === "resolved",
            "text-warning-600": variant === "pending",
            "hidden text-success-700": variant === "clik-resolved",
            "hidden text-subtext-color": variant === "clik-dismissed",
            "text-subtext-color group-active/f7edaefb:text-warning-600 group-focus-within/f7edaefb:text-warning-600":
              variant === "clk-pending",
          }
        )}
        name="FeatherHourglass"
      />
      <SubframeCore.Icon
        className={SubframeUtils.twClassNames(
          "text-body font-body text-default-font",
          {
            hidden:
              variant === "dismissed" ||
              variant === "pending" ||
              variant === "clik-dismissed" ||
              variant === "clk-pending",
            "text-success-700": variant === "resolved",
            "text-subtext-color group-active/f7edaefb:text-success-700 group-focus-within/f7edaefb:text-success-700":
              variant === "clik-resolved",
          }
        )}
        name="FeatherCheck"
      />
      <SubframeCore.Icon
        className={SubframeUtils.twClassNames(
          "text-body font-body text-default-font",
          {
            hidden:
              variant === "resolved" ||
              variant === "pending" ||
              variant === "clik-resolved" ||
              variant === "clk-pending",
            "text-subtext-color group-active/f7edaefb:text-default-font group-focus-within/f7edaefb:text-default-font":
              variant === "clik-dismissed",
          }
        )}
        name="FeatherXCircle"
      />
      {text ? (
        <span
          className={SubframeUtils.twClassNames(
            "text-body-bold font-body-bold text-default-font",
            {
              "text-success-700": variant === "resolved",
              "text-warning-600": variant === "pending",
              "text-subtext-color group-active/f7edaefb:text-success-700 group-focus-within/f7edaefb:text-success-700":
                variant === "clik-resolved",
              "text-subtext-color group-active/f7edaefb:text-default-font group-focus-within/f7edaefb:text-default-font":
                variant === "clik-dismissed",
              "text-subtext-color group-active/f7edaefb:text-warning-600 group-focus-within/f7edaefb:text-warning-600":
                variant === "clk-pending",
            }
          )}
        >
          {text}
        </span>
      ) : null}
    </div>
  );
});

export const TagClickLogStatus = TagClickLogStatusRoot;
